import React, { useEffect, useMemo, useRef } from 'react'
import { useTutorialContext } from 'context/TutorialContext'
import { cn } from '@utils/safeTs'
import { Close, Info } from '@components/icons'
import styles from './TutorialItem.module.sass'


interface Props {
  tutorialKey: string
  index: number
  direction?: 'top' | 'bottom' | 'left' | 'right' | 'right-top'
  content?: string | React.ReactNode
  children?: React.ReactNode
  className?: string
  overlayClassName?: string
}

const TutorialItem = (props: Props) => {

  const { 
    tutorialKey,
    index,
    direction = 'right',
    content,
    children,
    className = '',
    overlayClassName = '',
  } = props

  const ref = useRef(null)
  const overlayRef = useRef(null)
  const { tutorialItems, nextTutorialItem } = useTutorialContext()
  const open = useMemo(() => (!tutorialItems[tutorialKey] && index == 1) || (tutorialItems[tutorialKey] == index), [tutorialItems, tutorialKey, index])

  const setPosition = () => {
    if (ref?.current && overlayRef?.current){
      const targetRect = (ref.current as HTMLDivElement).getBoundingClientRect()
      const overlayEl = overlayRef.current as HTMLDivElement
      const overlayRect = overlayEl.getBoundingClientRect()

      // Se non ha le misure vuol dire che non è visibile in pagina => non posso calcolare la posizione
      if (targetRect.width && targetRect.height){

        const gap = 10
        let top = targetRect.top + (targetRect.height - overlayRect.height) / 2
        let left = targetRect.right + gap
        if (direction === 'left' || direction === 'right') {
          top = targetRect.top + (targetRect.height - overlayRect.height) / 2
        }

        switch (direction) {
          case 'top':
            top = targetRect.top - overlayRect.height - gap
            left = targetRect.left + (targetRect.width - overlayRect.width) / 2
            break
          case 'bottom':
            top = targetRect.bottom + gap
            left = targetRect.left + (targetRect.width - overlayRect.width) / 2
            break
          case 'left':
            top = targetRect.top + (targetRect.height - overlayRect.height) / 2
            left = targetRect.left - overlayRect.width - gap
            break
          case 'right-top':
            top = targetRect.bottom - overlayRect.height
            left = targetRect.right + gap
            break
          case 'right':
          default:
            top = targetRect.top + (targetRect.height - overlayRect.height) / 2
            left = targetRect.right + gap
        }

        overlayEl.style.top = `${top}px`
        overlayEl.style.left = `${left}px`

      }
    }
  }

  useEffect(() => {
    if (open) 
      setPosition()
  }, [open])

  // questo cambia se l'elemento con il tutorialItem diventa visibile o meno
  useEffect(() => {
    setPosition()
  }, [ref.current?.getBoundingClientRect()])

  useEffect(() => {
    const handleResize = () => setPosition()
    setPosition()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  return (
    <div className={cn(styles.root, styles[`root--${direction}`], open ? styles['root--open'] : '', className)} ref={ref}>
      <div className={cn(styles.overlay, overlayClassName)} ref={overlayRef}>
        <div className={styles.arrow} />
        <div className={styles.icon}>
          <Info />
        </div>
        <div className={styles.content}>
          {content}
        </div>
        <button type="button" className={styles.close} onClick={() => nextTutorialItem(tutorialKey, index + 1)}>
          <Close />
        </button>
      </div>
      {children}
    </div>
  )
}

export default TutorialItem

